import {extendTheme} from '@chakra-ui/react';

// Custom opalite color scale generated from #1E9FD4 as the 500.
const opalite = {
  50: '#E8F5FB',
  100: '#C5E7F6',
  200: '#93D2EE',
  300: '#5FBCE3',
  400: '#38ADDC',
  500: '#1E9FD4',
  600: '#117DB1',
  700: '#0B6A96',
  800: '#085D85',
  900: '#064360',
};

const theme = extendTheme({
  config: {
    initialColorMode: 'light',
    useSystemColorMode: false,
  },

  colors: {
    opalite,
    // Workflow accent colors — the `500` values preserve the iOS palette,
    // while `700`/`300` are the accessible text/stripe variants used on
    // light/dark planner surfaces.
    scribe: {
      100: '#E8E9FD',
      300: '#A5A9F4',
      500: '#6366F1',
      600: '#4F52D6',
      700: '#4144B5',
    },
    interp: {
      100: '#E6F4EF',
      300: '#9FD3C2',
      500: '#5EAF96',
      600: '#4A9079',
      700: '#3C7563',
    },
    ehr: {
      100: '#E0F3EA',
      300: '#86D3AE',
      500: '#22A06B',
      600: '#1B8456',
      700: '#166B46',
    },
  },

  fonts: {
    heading: "'Figtree', sans-serif",
    body: "'Figtree', sans-serif",
    // Real monospace for `<Code>` / `<Kbd>` so cron expressions, IANA
    // timezones and IDs in the super-admin panels are visually distinct
    // from body text instead of blending in as Figtree.
    mono: "'JetBrains Mono', 'SFMono-Regular', 'Menlo', 'Consolas', monospace",
  },

  // iOS-aligned type scale. We override `2xs` (Chakra default 10px) up
  // to 11px because the EHR Integration table renders fine-print
  // metadata ("your time: …", non-ET hints, error one-liners) at 2xs;
  // 10px is genuinely uncomfortable to read at normal viewing distance.
  fontSizes: {
    '2xs': '0.6875rem', // 11px — fine-print metadata
    xs: '0.8125rem', // 13px — footnote / badges
    sm: '0.9375rem', // 15px — subheadline / metadata
    md: '1.0625rem', // 17px — body / headline
    lg: '1.25rem', // 20px — title3
    xl: '1.375rem', // 22px — title2
    '2xl': '1.625rem', // 26px — title
    '3xl': '2.125rem', // 34px — largeTitle
  },

  styles: {
    global: {
      body: {
        // Re-state these on `body` so the Chakra ColorModeScript
        // wrapper (which sets its own bg/color) doesn't strip the
        // smoothing + optimizeLegibility hints we set in index.css.
        // `auto` (vs the old `antialiased`/`grayscale`) lets the OS render
        // Figtree at full weight — the thinned text was hard to read,
        // especially for older clinicians.
        WebkitFontSmoothing: 'auto',
        MozOsxFontSmoothing: 'auto',
        textRendering: 'optimizeLegibility',
        fontFeatureSettings: "'kern', 'liga', 'cv11'",
      },
      // Tabular numerals everywhere we render data: stat tiles, table
      // cells, badges with counts. Without `tnum` Figtree renders
      // proportional digits, so columns of timestamps and durations
      // wobble visibly between rows.
      'table, .chakra-stat__number, .chakra-heading': {
        fontVariantNumeric: 'tabular-nums',
      },
    },
  },

  components: {
    Card: {
      baseStyle: {
        container: {
          borderRadius: 'xl',
          border: '0.5px solid',
          borderColor: 'blackAlpha.100',
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        },
      },
    },

    Badge: {
      baseStyle: {
        borderRadius: 'full',
        fontWeight: 'normal',
        textTransform: 'none',
      },
    },

    CloseButton: {
      baseStyle: {
        bg: 'transparent',
        borderRadius: '0',
        borderWidth: '0',
        _hover: {
          bg: 'transparent',
        },
        _active: {
          bg: 'transparent',
        },
        _focus: {
          bg: 'transparent',
        },
      },
    },

    FormError: {
      baseStyle: {
        text: {
          color: 'red.600',
          _dark: {color: 'red.300'},
        },
      },
    },

    Button: {
      baseStyle: {
        // `medium` (500) instead of `normal` (400): button labels are calls
        // to action and need to read as tappable/clickable affordances, not
        // as body copy. The extra weight is a meaningful legibility win for
        // older clinicians without looking heavy.
        fontWeight: 'medium',
        borderRadius: 'lg',
        transition:
          'background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease, box-shadow 0.15s ease, opacity 0.15s ease, transform 0.15s ease',
        _focusVisible: {
          boxShadow: '0 0 0 3px var(--chakra-colors-opalite-600)',
          _dark: {
            boxShadow: '0 0 0 3px var(--chakra-colors-opalite-300)',
          },
        },
      },
      variants: {
        opalitePrimary: {
          bg: 'opalite.600',
          color: 'white',
          _hover: {
            bg: 'opalite.700',
          },
          _active: {
            bg: 'opalite.700',
          },
          _dark: {
            bg: 'opalite.500',
            color: 'gray.900',
            _hover: {
              bg: 'opalite.500',
            },
            _active: {
              bg: 'opalite.500',
            },
          },
        },
        opaliteGhost: {
          color: 'opalite.600',
          _hover: {
            bg: 'opalite.50',
          },
          _dark: {
            color: 'opalite.500',
            _hover: {
              bg: 'whiteAlpha.200',
            },
          },
        },
      },
    },

    Modal: {
      baseStyle: {
        dialog: {
          borderRadius: '2xl',
        },
        overlay: {
          bg: 'blackAlpha.400',
          backdropFilter: 'blur(6px)',
        },
      },
    },

    Input: {
      defaultProps: {
        focusBorderColor: 'opalite.600',
      },
    },

    Select: {
      defaultProps: {
        focusBorderColor: 'opalite.600',
      },
    },

    Textarea: {
      defaultProps: {
        focusBorderColor: 'opalite.600',
      },
    },

    // Slightly heavier, slightly more letter-spaced uppercase column
    // headers. Matches the visual weight of the JetBrains Mono data
    // values rendered in the rows below so the header doesn't visually
    // recede into the background.
    Table: {
      baseStyle: {
        th: {
          fontWeight: 'normal',
          letterSpacing: '0.06em',
          color: 'gray.600',
        },
      },
    },

    // The default Chakra Code background (`gray.100` at the same size
    // as body text) made `<Code>` blend into the row. Bumping size to
    // 0.9em + slight horizontal padding keeps it scannable as data
    // without dominating the cell.
    Code: {
      baseStyle: {
        fontSize: '0.9em',
        px: '0.35em',
        py: '0.05em',
        borderRadius: 'md',
      },
    },
  },
});

export default theme;
